// @flow
import * as React from 'react';
import { Row, Col, Button, Form, Label } from 'react-bootstrap';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import CopyToClipboard from 'react-copy-to-clipboard';
import { CSSTransitionGroup } from 'react-transition-group';
import Input from '../../components/Input';
import {
  convertValueToBoolean,
  convertValueToNumber,
  validateRequired,
  validateFileName,
  validateABN,
  validateACN,
  validatePhoneNumber
} from '../../helpers';
import type { Organisation } from '../../types';

type Props = {
  currentCustomer: Organisation,
  successMsg: string,
  errorMsg: string,
  abn: ?string,
  isInternalAdminUser: boolean,
  isNew: boolean,
  isSaving: boolean,
  handleSubmit: (v: Object) => Promise<void>
};

type State = {
  copied: boolean
};

class CustomerDetailsForm extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      copied: false
    };
  }
  render() {
    const {
      settings: { industries, organisationTypes },
      id
    } = this.props.currentCustomer;

    const isAgency =
      organisationTypes &&
      organisationTypes.filter(
        t =>
          t.id === this.props.currentCustomer.organisationTypeId &&
          t.name === 'Agency'
      ).length > 0;

    return (
      <div>
        <Row />
        <Form onSubmit={this.props.handleSubmit}>
          {!this.props.isNew && (
            <Row>
              <Col sm={12}>
                <header className="form-input no-input">
                  <strong>Customer ID: </strong>&nbsp;<small>{id}</small>
                  <CopyToClipboard
                    text={id}
                    className="copy-clipboard"
                    onCopy={() => this.setState({ copied: true })}
                  >
                    <span>
                      <i className="glyphicon glyphicon-copy" />
                      Copy ID
                    </span>
                  </CopyToClipboard>
                  {this.state.copied ? (
                    <span className="copy-success">Copied.</span>
                  ) : null}
                </header>
              </Col>
            </Row>
          )}
          <Row>
            <Col sm={12} className="form-input">
              <Field
                component={Input}
                type="text"
                label="Business/Organisation Name"
                placeholder="Business/Organisation Name"
                name="name"
                validate={[validateRequired, validateFileName]}
              />
            </Col>
          </Row>

          <Row>
            <Col sm={12} className="form-input">
              <Field
                component={Input}
                type="toggle"
                name="isArchived"
                label="Active"
                reverse
              />
            </Col>
          </Row>

          {!isAgency && (
            <Row>
              <Col sm={12} className="form-input">
                <Field
                  component={Input}
                  type="toggle"
                  label="Request Agency Status (Mailing Agent, Media Agency)"
                  name="agencyRequest"
                  format={convertValueToBoolean}
                />
              </Col>
            </Row>
          )}

          {isAgency && !this.props.isInternalAdminUser && (
            <Row>
              <Col sm={12} className="form-input">
                <Field
                  component={Input}
                  type="toggle"
                  label="Is Agency (Mailing Agent, Media Agency)"
                  name="isAgency"
                  format={convertValueToBoolean}
                  disabled={true}
                  input={{ value: true }}
                />
              </Col>
            </Row>
          )}

          {this.props.isInternalAdminUser && (
            <div>
              <Row>
                <Col sm={12} className="form-input">
                  <Field
                    component={Input}
                    type="radioGroup"
                    name="organisationTypeId"
                    label="Role"
                    normalize={convertValueToNumber}
                    options={organisationTypes}
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={12} className="form-input">
                  <strong className="mb-20">Unaddressed Mail</strong>
                  <Field
                    component={Input}
                    type="toggle"
                    name="canSendToNoJunkMail"
                    label="Can send to 'No Junk Mail' addresses"
                  />
                </Col>
              </Row>
              <Row />
              <Row>
                <Col sm={12} className="form-input">
                  <strong className="mb-20">Addressed Mail</strong>
                  <Field
                    component={Input}
                    type="toggle"
                    name="canSendToNoJunkMailAcquistion"
                    label="Can send to 'No Junk Mail' addresses"
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={12} className="form-input">
                  <Field
                    component={Input}
                    type="toggle"
                    name="canSendToDoNotMail"
                    label="Can send to 'Do Not Mail' addresses"
                  />
                </Col>
              </Row>
            </div>
          )}
          {this.props.isInternalAdminUser && (
            <Row>
              <Col sm={12} className="form-input">
                <Field
                  component={Input}
                  type="number"
                  min="0"
                  label="Organisation Record Download Limit"
                  name="organisationAddressRecordDownloadLimit"
                />
              </Col>
            </Row>
          )}
          {this.props.isInternalAdminUser && (
            <Row>
              <Col sm={12} className="form-input">
                <Field
                  component={Input}
                  type="number"
                  min="0"
                  label="Organisation Download Limit Time Period"
                  name="organisationAmDownloadLimitTimePeriod"
                />
              </Col>
            </Row>
          )}
          {this.props.isInternalAdminUser && !this.props.abn && (
            <Row>
              <Col sm={12} className="form-input">
                <Field
                  component={Input}
                  type="checkbox"
                  label="Allow registration without ABN"
                  name="allowRegistrationWithoutAbn"
                />
              </Col>
              <Col sm={12} className="form-input">
                <Field
                  component={Input}
                  componentClass="textarea"
                  type="textarea"
                  label="Reason"
                  name="reasonForNoAbn"
                />
              </Col>
            </Row>
          )}
          {this.props.isInternalAdminUser && this.props.abn && (
            <Row>
              <Col sm={12} className="form-input">
                <Field
                  component={Input}
                  type="checkbox"
                  label="Allow registration with same ABN"
                  name="allowRegistrationWithSameAbn"
                />
              </Col>
              <Row />
              <Col sm={12} className="form-input">
                <Field
                  component={Input}
                  componentClass="textarea"
                  type="textarea"
                  label="Reason"
                  name="reasonForSameAbn"
                />
              </Col>
            </Row>
          )}
          <Row>
            <Col sm={12} className="form-input">
              <Field
                component={Input}
                type="text"
                label="ABN"
                placeholder="ABN"
                name="abn"
                validate={validateABN}
                parse={val => val && val.replace(/\s+/g, '')}
                format={val => val && val.replace(/\s+/g, '')}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={12} className="form-input">
              <Field
                component={Input}
                type="text"
                label="ACN"
                placeholder="ACN"
                name="acn"
                validate={validateACN}
              />
            </Col>
          </Row>

          <Row>
            <Col sm={12} className="form-input">
              <Field
                component={Input}
                type="autocomplete"
                label="Address"
                placeholder="Address"
                name="address"
                validate={[validateRequired]}
              />
            </Col>
          </Row>

          <Row>
            <Col sm={12} className="form-input">
              <Field
                component={Input}
                type="phone"
                label="Phone"
                placeholder="Phone"
                name="phone"
                validate={[validateRequired, validatePhoneNumber]}
              />
            </Col>
          </Row>

          <Row>
            <Col sm={12} className="form-input">
              <Field
                component={Input}
                type="select"
                label="Industry"
                name="industryId"
                componentClass="select"
                placeholder="select"
                normalize={convertValueToNumber}
                options={industries}
                validate={validateRequired}
                sort
                prompt
              />
            </Col>
          </Row>

          <Row>
            <Col sm={12} className="form-input">
              <Button
                bsStyle="primary"
                type="submit"
                disabled={this.props.isSaving}
              >
                {this.props.isSaving ? 'Saving...' : 'Save'}
              </Button>
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <CSSTransitionGroup
                transitionName="fade"
                transitionEnterTimeout={300}
                transitionLeaveTimeout={300}
              >
                {this.props.successMsg && (
                  <p className="text-success">{this.props.successMsg}</p>
                )}
              </CSSTransitionGroup>
            </Col>
          </Row>
        </Form>
        {this.props.errorMsg && (
          <div className="alert alert-danger">{this.props.errorMsg}</div>
        )}
      </div>
    );
  }
}

// Decorate the form component
CustomerDetailsForm = reduxForm({
  form: 'customerDetailsForm',
  validate: values => {
    const errors = {};
    let agency;
    let agencyId;
    if (values && values.settings && values.settings.organisationTypes) {
      agency = values.settings.organisationTypes.find(o => o.name === 'Agency');
      if (agency) {
        agencyId = agency.id;
      }
    }
    if (
      (values.agencyRequest || values.organisationTypeId === agencyId) &&
      !values.abn
    ) {
      errors.abn = 'ABN is required';
    }
    return errors;
  }
})(CustomerDetailsForm);

const selector = formValueSelector('customerDetailsForm');

CustomerDetailsForm = connect(state => {
  const abn = selector(state, 'abn');
  return {
    abn
  };
})(CustomerDetailsForm);

export default CustomerDetailsForm;



// WEBPACK FOOTER //
// ./src/containers/customer/CustomerDetailsForm.js